import React from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { FUEL_COLOR, ownerLabel } from './constants';
import { useSaleCompanyName } from '../../../utils/saleCompany';

export default function VehicleListDialog({
  open,
  onClose,
  vehicles,
  otherCompanyVehicles,
  employees,
  openAdd,
  openEdit,
  handleSetDefault,
  handleDeleteVehicle,
}) {
  const companyName = useSaleCompanyName();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <Box sx={{
        px: 3,
        py: 2,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CarIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.1}>รายการรถยนต์</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {vehicles.length} คันของฉัน
              {otherCompanyVehicles.length > 0 && ` · ${otherCompanyVehicles.length} คันบริษัท (ส่วนกลาง)`}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={openAdd}
            size={isMobile ? 'small' : 'medium'}
            sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 700, '&:hover': { bgcolor: 'grey.100' } }}
          >
            {isMobile ? 'เพิ่ม' : 'เพิ่มรถยนต์'}
          </Button>
          {isMobile && (
            <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {vehicles.length === 0 && otherCompanyVehicles.length === 0 ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <CarIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">ยังไม่มีรถยนต์ กด "เพิ่มรถยนต์" เพื่อเริ่มต้น</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 14, py: 1.5 }}>ทะเบียนรถ</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 14, py: 1.5 }}>ยี่ห้อ / รุ่น</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 14, py: 1.5 }}>เชื้อเพลิง</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 14, py: 1.5 }}>บาท/กม.</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 14, py: 1.5 }}>เจ้าของ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 14, py: 1.5 }}>รถหลัก</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 14, py: 1.5 }}>จัดการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{
                      py: 1,
                      px: 3,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    }}>
                      <Typography variant="body2" fontWeight={700} color="primary.main" fontSize={13}>
                        🚗 รถของฉัน — {vehicles.length} คัน
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {vehicles.map((v, idx) => (
                  <TableRow key={`my-${idx}`} sx={{
                    bgcolor: v.is_default ? alpha(theme.palette.warning.main, 0.06) : undefined,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  }}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={700} fontSize={15} color="primary.main">{v.license_plate}</Typography>
                        {v.is_default && (
                          <Chip
                            icon={<StarIcon sx={{ fontSize: '13px !important' }} />}
                            label="หลัก"
                            size="small"
                            color="warning"
                            sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography fontSize={14} fontWeight={500}>{v.brand || '—'}</Typography>
                      {v.model_name && <Typography variant="caption" color="text.secondary" fontSize={12}>{v.model_name}</Typography>}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip label={v.fuel_type || 'เบนซิน'} color={FUEL_COLOR[v.fuel_type] || 'default'} sx={{ fontSize: 13, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Typography fontSize={14} fontWeight={600}>{v.rate_per_km ?? 4}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography fontSize={13}>{ownerLabel(v, employees, companyName)}</Typography>
                      <Chip
                        label={v.owner_type === 'company' ? 'บริษัท' : v.owner_type === 'personal' ? 'ส่วนตัว' : 'อื่นๆ'}
                        variant="outlined"
                        size="small"
                        color={v.owner_type === 'company' ? 'primary' : v.owner_type === 'personal' ? 'success' : 'default'}
                        sx={{ fontSize: 11, height: 20, mt: 0.4 }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Tooltip title={v.is_default ? 'รถหลักอยู่แล้ว' : 'ตั้งเป็นรถหลัก'}>
                        <IconButton onClick={() => handleSetDefault(idx)} color={v.is_default ? 'warning' : 'default'}>
                          {v.is_default ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="แก้ไข">
                          <IconButton color="primary" onClick={() => openEdit(idx)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="ลบ">
                          <IconButton color="error" onClick={() => handleDeleteVehicle(idx)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {otherCompanyVehicles.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{
                      py: 1,
                      px: 3,
                      bgcolor: alpha(theme.palette.info.main, 0.08),
                      borderLeft: `4px solid ${theme.palette.info.main}`,
                    }}>
                      <Typography variant="body2" fontWeight={700} color="info.main" fontSize={13}>
                        🏢 รถบริษัท (ส่วนกลาง) — {otherCompanyVehicles.length} คัน · ทุกคนสามารถใช้ได้
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {otherCompanyVehicles.map((v, idx) => (
                  <TableRow key={`company-${idx}`} sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.02),
                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.06) },
                  }}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography fontWeight={700} fontSize={15} color="info.main">{v.license_plate}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography fontSize={14} fontWeight={500}>{v.brand || '—'}</Typography>
                      {v.model_name && <Typography variant="caption" color="text.secondary" fontSize={12}>{v.model_name}</Typography>}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip label={v.fuel_type || 'เบนซิน'} color={FUEL_COLOR[v.fuel_type] || 'default'} sx={{ fontSize: 13, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Typography fontSize={14} fontWeight={600}>{v.rate_per_km ?? 4}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip label="บริษัท" variant="outlined" color="primary" sx={{ fontSize: 12 }} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Typography color="text.disabled">—</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Typography color="text.disabled">—</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="large">ปิด</Button>
      </DialogActions>
    </Dialog>
  );
}
